// Meta system changes must follow docs/META_AI_MASTERFILE.md.
import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, ExternalLink, Database, FileText, AlertTriangle, Brain, Sparkles, Target, Zap, Shield, Swords, CopyPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';

const apiBase = import.meta.env.VITE_API_BASE_URL || '';
const imgBase = apiBase || ''; // in dev: relative (vite proxy), in prod: absolute
const REPORT_TIERS = ['S', 'A', 'B', 'C', 'D', 'E'];

function metaAdminHeaders(extra = {}) {
  const token = localStorage.getItem('metaAdminToken') || localStorage.getItem('token');
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
}

function tierColorClass(tier) {
  if (tier === 'S') return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
  if (tier === 'A') return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
  if (tier === 'B') return 'bg-gray-500/15 text-gray-300 border-gray-500/30';
  if (tier === 'C') return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
  if (tier === 'D') return 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30';
  return 'bg-red-500/15 text-red-300 border-red-500/30';
}

function reportChampionTierList(report) {
  return report?.championTierList || report?.legendTierList || report?.tierList || {};
}

function reportDeckTierList(report) {
  return report?.deckTierList || {};
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

export default function Meta() {
  const { isAuthenticated, token } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [report, setReport] = useState(null);
  const [sources, setSources] = useState([]);
  const [aiStatus, setAiStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [savingDeckKey, setSavingDeckKey] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadMeta = async () => {
    setError('');
    setSuccess('');
    const [sourcesResponse, snapshotResponse, analysisResponse, reportResponse, aiStatusResponse] = await Promise.all([
      fetch(`${apiBase}/api/meta/sources`),
      fetch(`${apiBase}/api/meta/snapshot?deckLimit=30&articleLimit=80`),
      fetch(`${apiBase}/api/meta/analysis`),
      fetch(`${apiBase}/api/meta/report`).catch(() => null),
      fetch(`${apiBase}/api/meta/ai-status`).catch(() => null),
    ]);
    if (!sourcesResponse.ok || !snapshotResponse.ok || !analysisResponse.ok) {
      throw new Error('Meta API non disponibile');
    }
    const sourcesJson = await sourcesResponse.json();
    const snapshotJson = await snapshotResponse.json();
    const analysisJson = await analysisResponse.json();
    setSources(sourcesJson.sources || []);
    setSnapshot(snapshotJson);
    setAnalysis(analysisJson.analysis || null);

    if (reportResponse?.ok) {
      try {
        const reportJson = await reportResponse.json();
        setReport(reportJson?.report || null);
      } catch (_) {
        setReport(null);
      }
    } else {
      setReport(null);
    }

    if (aiStatusResponse?.ok) {
      try {
        const statusJson = await aiStatusResponse.json();
        setAiStatus(statusJson || null);
      } catch (_) {
        setAiStatus(null);
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    loadMeta()
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${apiBase}/api/meta/refresh`, {
        method: 'POST',
        headers: metaAdminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ dotgg: { limit: 30 }, aiReport: { wait: true } }),
      });
      if (!response.ok) throw new Error('Refresh fallito');
      await loadMeta();
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const generateReport = async () => {
    setGeneratingReport(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${apiBase}/api/meta/report`, {
        method: 'POST',
        headers: metaAdminHeaders({ 'Content-Type': 'application/json' }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message || 'Generazione report fallita');
      await loadMeta();
    } catch (err) {
      setError(err.message);
    } finally {
      setGeneratingReport(false);
    }
  };

  const addDeckToCollection = async (deck, key) => {
    setError('');
    setSuccess('');
    if (!isAuthenticated || !token) {
      setError('Effettua il login per aggiungere il mazzo ai tuoi mazzi.');
      return;
    }
    if (!deck?.cards || Object.keys(deck.cards).length === 0) {
      setError('Questo Tier Deck non ha ancora una lista carte importabile. Rigenera il report Meta.');
      return;
    }

    setSavingDeckKey(key);
    try {
      const response = await fetch(`${apiBase}/api/decks/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: deck.sourceDeckName || deck.archetype || deck.name || 'Meta Deck',
          description: `Importato dal report Meta AI${report?.generatedAt ? ` del ${formatDate(report.generatedAt)}` : ''}.`,
          cards: deck.cards,
          sourceUrl: deck.sourceUrl,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message || 'Import del mazzo fallito');
      setSuccess(`Mazzo aggiunto: ${payload.deck?.name || deck.sourceDeckName || deck.archetype}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingDeckKey('');
    }
  };

  const enabledSources = useMemo(
    () => sources.filter((source) => source.enabled),
    [sources]
  );

  return (
    <div className="py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold mb-2">Meta Intelligence</h1>
            <p className="text-muted-foreground">
              Dati verificabili, report AI e segnali competitivi per leggere il meta.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button onClick={generateReport} disabled={generatingReport || refreshing || !aiStatus?.available} variant="outline" className="gap-2">
              <Brain className={`h-4 w-4 ${generatingReport ? 'animate-pulse' : ''}`} />
              Genera report AI
            </Button>
            <Button onClick={refresh} disabled={refreshing || generatingReport} className="gap-2">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Aggiorna fonti
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded border border-red-500/40 bg-red-950/30 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 rounded border border-green-500/40 bg-green-950/30 px-4 py-3 text-sm text-green-200">
            {success}
          </div>
        )}

        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <Card className="bg-card/50 border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Database className="h-4 w-4" /> Fonti attive
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{enabledSources.length}</div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Mazzi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{snapshot?.counts?.decks ?? 0}</div>
              <div className="text-xs text-muted-foreground mt-1">{snapshot?.counts?.tournamentDecks ?? 0} da tornei</div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4" /> Report
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{snapshot?.counts?.articles ?? 0}</div>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Ultimo refresh</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground">{formatDate(snapshot?.refreshedAt)}</div>
            </CardContent>
          </Card>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="bg-card/50 border-border/50 lg:col-span-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Shield className="h-5 w-5" /> Brief deterministico
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{analysis?.brief?.summary || 'Nessun brief disponibile.'}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="rounded bg-background/60 px-2 py-1 text-muted-foreground">
                  Dati: {analysis?.sampleSize?.decks ?? 0} mazzi · {analysis?.sampleSize?.tournamentDecks ?? 0} tornei · {analysis?.sampleSize?.articles ?? 0} segnali
                </span>
                <span className={`rounded px-2 py-1 ${aiStatus?.available ? 'bg-green-500/15 text-green-300' : 'bg-amber-500/15 text-amber-300'}`}>
                  AI: {aiStatus?.available ? `disponibile (${aiStatus.model})` : 'non disponibile'}
                </span>
                {report?.generatedAt && (
                  <span className="rounded bg-primary/10 px-2 py-1 text-primary">
                    Report: {formatDate(report.generatedAt)}
                  </span>
                )}
              </div>
              {(analysis?.brief?.blockedSources || []).length > 0 && (
                <div className="mt-3 rounded border border-yellow-500/30 bg-yellow-950/20 px-3 py-2 text-sm text-yellow-100">
                  Fonti da sistemare: {analysis.brief.blockedSources.join(', ')}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {report ? (
            <>
              <Card className="bg-card/50 border-primary/30 lg:col-span-3">
                <CardHeader className="border-b border-border/30 pb-3">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Brain className="h-5 w-5 text-primary" /> Report AI Settimanale
                    <span className="ml-auto text-xs font-normal text-muted-foreground">
                      {report.modelUsed} · {formatDate(report.generatedAt)}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-3">
                      <span className={`rounded px-2 py-0.5 text-xs font-bold ${
                        report.metaSummary?.metaHealth === 'balanced' ? 'bg-green-500/15 text-green-300' :
                        report.metaSummary?.metaHealth === 'centralized' ? 'bg-amber-500/15 text-amber-300' :
                        'bg-blue-500/15 text-blue-300'
                      }`}>
                        Meta: {report.metaSummary?.metaHealth || '—'}
                      </span>
                      <span className="rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">
                        {report.sampleSize?.decks ?? 0} mazzi
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {report.metaSummary?.overview || 'Nessuna panoramica.'}
                    </p>
                    {report.metaSummary?.bannedCardsImpact && (
                      <div className="rounded border border-red-500/20 bg-red-950/10 px-3 py-2 text-xs text-red-200">
                        {report.metaSummary.bannedCardsImpact}
                      </div>
                    )}
                    {(report.metaSummary?.dominantArchetypes || []).length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                          <Swords className="h-3.5 w-3.5" /> Archetipi Dominanti
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {report.metaSummary.dominantArchetypes.map((a, i) => (
                            <span key={i} className={`rounded px-2 py-1 text-xs font-medium ${
                              a.strength === 'dominant' ? 'bg-purple-500/15 text-purple-300' :
                              a.strength === 'strong' ? 'bg-blue-500/15 text-blue-300' :
                              'bg-gray-500/15 text-gray-300'
                            }`}>
                              {a.name} ({a.colors})
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card/50 border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Target className="h-4 w-4" /> Tier Campioni
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {REPORT_TIERS.map((tier) => {
                    const cards = reportChampionTierList(report)[tier] || [];
                    if (!cards.length) return null;
                    return (
                      <div key={tier}>
                        <div className={`inline-block rounded px-2 py-0.5 text-xs font-bold mb-1 border ${tierColorClass(tier)}`}>
                          Tier {tier}
                        </div>
                        <div className="space-y-2">
                          {cards.map((card, i) => (
                            <div key={i} className="flex items-center gap-3 pl-1">
                              {card.image && (
                                <img src={imgBase + card.image} alt={card.name} className="h-12 w-9 rounded object-cover shrink-0 border border-border/50" />
                              )}
                              <div className="text-xs text-muted-foreground">
                                <span className="font-semibold text-foreground">{card.name}</span>
                                {card.type ? ` · ${card.type}` : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card className="bg-card/50 border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Swords className="h-4 w-4" /> Tier Deck
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {REPORT_TIERS.map((tier) => {
                    const decks = reportDeckTierList(report)[tier] || [];
                    if (!decks.length) return null;
                    return (
                      <div key={tier}>
                        <div className={`inline-block rounded px-2 py-0.5 text-xs font-bold mb-1 border ${tierColorClass(tier)}`}>
                          Tier {tier}
                        </div>
                        <div className="space-y-2">
                          {decks.map((deck, i) => {
                            const deckKey = `${tier}-${i}-${deck.archetype || deck.name || 'deck'}`;
                            const canImport = isAuthenticated && !!deck.cards && Object.keys(deck.cards).length > 0;
                            return (
                              <div key={deckKey} className="flex items-start gap-3 pl-1">
                                {deck.legendImage && (
                                  <img src={imgBase + deck.legendImage} alt={deck.legend || deck.archetype} className="h-20 w-14 rounded object-cover shrink-0 border border-border/50" />
                                )}
                                <div className="min-w-0 flex-1 text-xs text-muted-foreground">
                                  <div className="font-semibold text-foreground">{deck.archetype || deck.name}</div>
                                  {deck.legend ? <div className="text-muted-foreground/80">{deck.legend}</div> : null}
                                  {deck.colors ? <div className="text-muted-foreground/80">{deck.colors}</div> : null}
                                  {deck.sourceDeckName ? <div className="truncate text-muted-foreground/80">{deck.sourceDeckName}</div> : null}
                                  {deck.reason ? <div className="text-muted-foreground/70">{deck.reason}</div> : null}
                                </div>
                                {isAuthenticated && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8 shrink-0"
                                    disabled={!canImport || savingDeckKey === deckKey}
                                    onClick={() => addDeckToCollection(deck, deckKey)}
                                    title={canImport ? 'Aggiungi ai miei mazzi' : 'Lista carte non disponibile'}
                                    aria-label="Aggiungi ai miei mazzi"
                                  >
                                    <CopyPlus className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card className="bg-card/50 border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Zap className="h-4 w-4" /> Sinergie e Previsioni
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(report.cardSynergies || []).length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-amber-300 mb-1">Sinergie</div>
                      {report.cardSynergies.slice(0, 4).map((syn, i) => (
                        <div key={i} className="text-xs text-muted-foreground pl-1">
                          <span className="font-semibold text-foreground">{syn.cards?.join(' + ')}</span>
                          {syn.reason ? ` — ${syn.reason}` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                  {report.predictions && (
                    <div className="rounded border border-primary/20 bg-primary/5 p-2 text-xs text-muted-foreground">
                      <span className="font-semibold text-primary">Previsioni: </span>
                      {report.predictions}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-card/50 border-border/50 lg:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Sparkles className="h-5 w-5 text-primary" /> AI Report Settimanale
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Brain className="h-10 w-10 text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground">Nessun report AI disponibile.</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    {aiStatus?.available
                      ? 'Genera il primo report AI dai dati deterministici gia indicizzati.'
                      : 'Ollama non risulta disponibile: il brief deterministico resta consultabile.'}
                  </p>
                  {aiStatus?.available && (
                    <Button onClick={generateReport} disabled={generatingReport} className="mt-4 gap-2">
                      <Brain className={`h-4 w-4 ${generatingReport ? 'animate-pulse' : ''}`} />
                      Genera report AI
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="text-xl">Colori/domini</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(analysis?.colorMix || []).map((entry) => (
                <div key={entry.color} className="flex items-center justify-between rounded border border-border/50 bg-background/40 px-3 py-2">
                  <span className="font-semibold capitalize">{entry.color}</span>
                  <span className="text-sm font-bold text-primary">{entry.count}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="text-xl">Archetipi dominanti</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(analysis?.archetypes || []).slice(0, 8).map((archetype) => (
                <div key={archetype.label} className="rounded border border-border/50 bg-background/40 px-3 py-2">
                  <div className="font-semibold text-sm truncate">{archetype.label}</div>
                  <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span className="capitalize">{archetype.colors}</span>
                    <span className="font-bold text-primary">{archetype.count} mazzi ({archetype.percentage}%)</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="text-xl">Salute sorgenti</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(analysis?.sourceHealth || []).map((source) => (
                <div key={source.sourceId} className="rounded border border-border/50 bg-background/40 px-3 py-2">
                  <div className="font-semibold">{source.sourceId}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {source.decks} mazzi · {source.articles} report · {source.errors} errori
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="text-xl">Fonti</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {sources.map((source) => (
                <div key={source.id} className="rounded border border-border/50 bg-background/40 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold">{source.name}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{source.notes}</div>
                    </div>
                    <span className={`rounded px-2 py-1 text-xs ${source.enabled ? 'bg-green-500/15 text-green-300' : 'bg-gray-500/15 text-gray-400'}`}>
                      {source.enabled ? 'attiva' : 'manuale'}
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <AlertTriangle className="h-5 w-5" /> Errori sorgenti
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(snapshot?.errors || []).length === 0 ? (
                <div className="text-sm text-muted-foreground">Nessun errore nell'ultimo refresh.</div>
              ) : (
                snapshot.errors.map((err, index) => (
                  <div key={`${err.source}-${index}`} className="rounded border border-yellow-500/30 bg-yellow-950/20 p-3 text-sm">
                    <span className="font-semibold text-yellow-200">{err.source}</span>
                    <span className="text-muted-foreground">: {err.message}</span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="text-xl">Best Decks (qualità)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {loading ? <div className="text-muted-foreground">Caricamento...</div> : (analysis?.bestDecks || []).map((deck, i) => (
                <a
                  key={`${deck.sourceId}-${i}`}
                  href={deck.url || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded border border-border/50 bg-background/40 p-3 transition-colors hover:border-primary/60"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">
                        {deck.isTournament && <span className="text-amber-400 mr-1">[T]</span>}
                        {deck.name}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {deck.author ? `di ${deck.author} · ` : ''}
                        {deck.cardCount} carte · {formatDate(deck.date)}
                        {deck.views ? ` · ${(deck.views / 1000).toFixed(0)}k views` : ''}
                      </div>
                    </div>
                    <span className="mt-1 shrink-0 rounded bg-primary/20 px-2 py-0.5 text-xs font-bold text-primary">
                      {deck.qualityScore}
                    </span>
                  </div>
                </a>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="text-xl">Report e segnali meta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {loading ? <div className="text-muted-foreground">Caricamento...</div> : (snapshot?.articles || []).map((article) => (
                <a
                  key={`${article.sourceId}-${article.externalId}`}
                  href={article.url || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded border border-border/50 bg-background/40 p-3 transition-colors hover:border-primary/60"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold">{article.title}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{article.sourceId} · {article.category || 'signal'}</div>
                    </div>
                    <ExternalLink className="mt-1 h-4 w-4 text-muted-foreground" />
                  </div>
                </a>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
